import { useState } from "preact/hooks";
import { route } from "preact-router";
import type { RoutableProps } from "preact-router";
import { fetchReviewForEdit, updateReview } from "../api";
import { BrandHeader } from "../components/brandHeader";
import { PrimaryButton } from "../components/buttons";
import { LegalFooter } from "../components/footer";
import { HoneypotInput } from "../components/honeypotInput";
import { RatingsCard } from "../components/ratingsCard";
import { ReviewTextCard } from "../components/reviewTextCard";
import { STRINGS } from "../constants/strings";
import { useFormState } from "../hooks/useFormState";
import { allRatingsAnswered } from "../state/formState";
import { useResourceLoad } from "../hooks/useResourceLoad";
import { toEditPendingPath } from "../routes";
import type { ErrorType, GetReviewForEditOk } from "../types";
import { handleSubmitFailure } from "../utils/submitFailure";

interface Props extends RoutableProps {
  editToken?: string;
}

const REDIRECT_STATUSES: ErrorType[] = ["expired", "invalid"];

export function EditFormScreen({ editToken }: Props) {
  const { data: prefill, loadError } = useResourceLoad(
    editToken,
    fetchReviewForEdit
  );

  if (!prefill || !editToken) {
    return (
      <div class="tl-page">
        <div class="tl-page-centered">
          {loadError ? (
            <span class="tl-field__error" role="alert">
              {loadError}
            </span>
          ) : (
            <p>{STRINGS.loading}</p>
          )}
        </div>
      </div>
    );
  }

  return <EditForm editToken={editToken} prefill={prefill} />;
}

interface EditFormProps {
  editToken: string;
  prefill: GetReviewForEditOk;
}

function EditForm({ editToken, prefill }: EditFormProps) {
  const form = useFormState({
    ratings: prefill.ratings,
    freitext: prefill.freitext,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");

  const companyName = prefill.companyName;
  const canSubmit = allRatingsAnswered(form.state);
  const reviewer = prefill.reviewer;
  const reviewerName = reviewer
    ? `${reviewer.firstName} ${reviewer.lastName}`.trim()
    : "";

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await updateReview({
        editToken,
        ratings: form.state.ratings,
        freitext: form.state.freitext,
        website_url: websiteUrl,
      });
      if (res.status === "ok") {
        sessionStorage.setItem(
          `review-edit:${editToken}`,
          JSON.stringify({
            pendingId: res.pendingId,
            email: reviewer?.email,
          })
        );
        route(toEditPendingPath(editToken));
        return;
      }
      handleSubmitFailure(res, REDIRECT_STATUSES, setSubmitError);
    } catch {
      setSubmitError(STRINGS.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div class="tl-page">
      <BrandHeader
        companyName={companyName}
        companyLogoUrl={prefill.companyLogoUrl}
        title={STRINGS.editTitle}
        subtitle={STRINGS.editSubtitle}
      />
      <form class="tl-page-main" onSubmit={onSubmit}>
        <RatingsCard
          companyName={companyName}
          ratings={form.state.ratings}
          onChange={form.setRating}
        />

        <ReviewTextCard value={form.state.freitext} onInput={form.setFreitext} />

        <div class="tl-card">
          <div class="tl-info-card">
            <span class="material-icons" aria-hidden="true">
              person
            </span>
            <span>
              {prefill.anonymous
                ? STRINGS.editReviewerAnonymous
                : STRINGS.editReviewerInfo(reviewerName, reviewer?.email ?? "")}
            </span>
          </div>
        </div>

        <HoneypotInput value={websiteUrl} onInput={setWebsiteUrl} />

        {submitError && (
          <div class="tl-field__error" role="alert">
            {submitError}
          </div>
        )}

        <div class="tl-submit-row">
          <PrimaryButton
            text={STRINGS.editSubmit}
            icon="send"
            type="submit"
            disabled={!canSubmit}
            loading={submitting}
          />
        </div>
      </form>
      <LegalFooter />
    </div>
  );
}
